import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { X, Loader2, Trash2, Key, UserPlus } from "lucide-react";

interface User {
  id: number;
  username: string;
  falApiKey: string;
  isAdmin: number;
  createdAt: string;
}

interface AdminPanelProps {
  onClose: () => void;
  currentUsername: string;
}

export function AdminPanel({ onClose, currentUsername }: AdminPanelProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newFalKey, setNewFalKey] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = async () => {
    try {
      const res = await fetch("/api/admin/users");
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setCreating(true);

    try {
      const res = await fetch("/api/admin/users", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: newUsername,
          password: newPassword,
          falApiKey: newFalKey,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || "Kreiranje korisnika neuspješno");
      }

      setNewUsername("");
      setNewPassword("");
      setNewFalKey("");
      await fetchUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kreiranje korisnika neuspješno");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (userId: number, username: string) => {
    if (username === currentUsername) return;
    if (!confirm(`Obrisati korisnika "${username}"?`)) return;

    try {
      const res = await fetch(`/api/admin/users/${userId}`, { method: "DELETE" });
      if (res.ok) {
        await fetchUsers();
      }
    } catch {
      // ignore
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6 backdrop-blur-sm"
      style={{ backgroundColor: "var(--effect-overlay-bg)" }}
      onClick={onClose}
    >
      <div
        className="rounded-2xl border border-border w-full max-w-lg max-h-[80vh] flex flex-col backdrop-blur-xl"
        style={{
          backgroundColor: "var(--effect-glass-surface-strong)",
          boxShadow: "var(--effect-glow-lg)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
          <h2 className="font-display text-lg font-bold">Upravljanje korisnicima</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {/* User list */}
          <div className="space-y-2">
            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Korisnici</Label>
            {loading ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-primary" />
              </div>
            ) : (
              <div className="space-y-2">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="flex items-center justify-between p-3 rounded-xl border border-border bg-secondary/20"
                  >
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="text-sm font-medium">
                          {user.username}
                          {!!user.isAdmin && (
                            <span className="ml-2 text-[10px] bg-primary/15 text-primary px-1.5 py-0.5 rounded-full">
                              admin
                            </span>
                          )}
                        </p>
                        <p className="text-[10px] text-muted-foreground">
                          {new Date(user.createdAt + "Z").toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {user.falApiKey && (
                        <Key className="w-3.5 h-3.5 text-green-500" title="Ima API ključ" />
                      )}
                      {user.username !== currentUsername && (
                        <button
                          onClick={() => handleDeleteUser(user.id, user.username)}
                          className="text-muted-foreground hover:text-destructive transition-colors"
                          title="Obriši korisnika"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Create user form */}
          <div className="rounded-xl border border-border bg-secondary/20 p-4">
            <h3 className="text-sm font-medium flex items-center gap-2 mb-3">
              <UserPlus className="w-4 h-4 text-primary" />
              Kreiraj korisnika
            </h3>
            <form onSubmit={handleCreateUser} className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Korisničko ime</Label>
                <Input
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="korisničko ime"
                  required
                  className="bg-secondary/30 border-border focus:border-primary/50"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Lozinka</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="lozinka"
                  required
                  className="bg-secondary/30 border-border focus:border-primary/50"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">FAL API Ključ</Label>
                <Input
                  value={newFalKey}
                  onChange={(e) => setNewFalKey(e.target.value)}
                  placeholder="fal-xxxx..."
                  required
                  className="bg-secondary/30 border-border focus:border-primary/50"
                />
              </div>
              {error && <p className="text-xs text-destructive">{error}</p>}
              <Button
                type="submit"
                size="sm"
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                style={{ boxShadow: "var(--effect-glow-sm)" }}
                disabled={creating}
              >
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                    Kreiranje...
                  </>
                ) : (
                  "Kreiraj korisnika"
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
